// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AnalyticsProvider, RouteTracker, CookieConsentBanner, extractTenant } from '@loa/analytics';
import { RelationshipsPage } from './pages/RelationshipsPage';
import { RoleBasedHomeRedirect } from './components/RoleBasedHomeRedirect';
import { RelationshipDetailPage } from './pages/RelationshipDetailPage';
import { CashflowWorkspacePage } from './pages/CashflowWorkspacePage';
import { featureFlags } from './config/featureFlags';
import { RelationshipWizardPage } from './pages/RelationshipWizardPage';
import { CoAWizardPage } from './pages/CoAWizardPage';
import { NewRequestPage } from './pages/NewRequestPage';
import ProposalDetailPage from './pages/ProposalDetailPage';
import { PensionProposalWizard } from './components/proposals/wizard/PensionProposalWizard';
import { PensionProposalBuilder } from './components/proposals/builder/PensionProposalBuilder';
import { QuoteBackedProposalBuilder } from './components/proposals/builder/QuoteBackedProposalBuilder';
import { BreakthroughReportBuilder } from './components/proposals/builder/BreakthroughReportBuilder';
import { SettingsPage } from './pages/SettingsPage';
import { ClientsListPage } from './pages/ClientsListPage';
import { ClientDetailPage } from './pages/ClientDetailPage';
import { ClientRiskAssessmentPage } from './pages/ClientRiskAssessmentPage';
import { ClientSustainabilityPage } from './pages/ClientSustainabilityPage';
import { ClientAmlPage } from './pages/ClientAmlPage';
import ClientDeepDivePage from './pages/ClientDeepDivePage';
import { PoliciesListPage } from './pages/PoliciesListPage';
import { PolicyDetailPage } from './pages/PolicyDetailPage';
import { LoginPage } from './pages/LoginPage';
import { RegistrationPage } from './pages/RegistrationPage';
import { RegistrationSuccessPage } from './pages/RegistrationSuccessPage';
import AwaitingApprovalPage from './pages/AwaitingApprovalPage';
import { ProviderAccessPage } from './pages/ProviderAccessPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { ProvidersListPage } from './pages/ProvidersListPage';
import { ProviderDetailPage } from './pages/ProviderDetailPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import AdvisorManagementPage from './pages/AdvisorManagementPage';
import AssignmentHistoryPage from './pages/AssignmentHistoryPage';
import AllLOAsPage from './pages/AllLOAsPage';
import TagsAdminPage from './pages/TagsAdminPage';
import { WizardSendInvitationsPage } from './pages/WizardSendInvitationsPage';
import { WizardCorporateHoldingsPage } from './pages/WizardCorporateHoldingsPage';
import { ResendInvitationPage } from './pages/ResendInvitationPage';
import { NudgeProviderPage } from './pages/NudgeProviderPage';
import { SupportDashboardPage } from './pages/SupportDashboardPage';
import { SupportTeamPage } from './pages/SupportTeamPage';
import { ActivityLogPage } from './pages/ActivityLogPage';
import FundsPage from './pages/FundsPage';
import FundDetailPage from './pages/FundDetailPage';
import PortfolioBuilderPage from './pages/PortfolioBuilderPage';
import { AdminViewRedirect } from './components/AdminViewRedirect';
import { AuthenticatedRouteLayout } from './components/AuthenticatedRouteLayout';
import { AuthenticatedRoute } from './components/AuthenticatedRoute';
import { BrandingProvider } from './contexts/BrandingContext';
import { AdminProvider } from './contexts/AdminContext';
import { CompanionLauncher } from './components/companion/CompanionLauncher';


function App() {
  return (
    <AnalyticsProvider config={{
      app: 'advisor-ui',
      tenant: extractTenant('advisor'),
      enabled: import.meta.env.VITE_ANALYTICS_ENABLED !== 'false',
      debug: import.meta.env.VITE_ANALYTICS_DEBUG === 'true',
      plausible: import.meta.env.VITE_PLAUSIBLE_DOMAIN ? {
        domain: import.meta.env.VITE_PLAUSIBLE_DOMAIN,
        apiHost: import.meta.env.VITE_PLAUSIBLE_API_HOST || undefined,
      } : undefined,
    }}>
      <CookieConsentBanner />
      <BrandingProvider>
        <BrowserRouter>
          <RouteTracker />
          <Routes>
            {/* Public routes - no authentication required */}
            <Route path='/login' element={<LoginPage />} />
            <Route path='/accept-invitation' element={<RegistrationPage />} />
            <Route path='/register' element={<AuthenticatedRoute><RegistrationSuccessPage /></AuthenticatedRoute>} />
            <Route path='/registration-success' element={<RegistrationSuccessPage />} />
            <Route path='/provider-access/:token' element={<ProviderAccessPage />} />
            <Route path='/awaiting-approval' element={<AuthenticatedRoute><AwaitingApprovalPage /></AuthenticatedRoute>} />
            <Route element={<AuthenticatedRouteLayout />}>
              <Route index element={<RoleBasedHomeRedirect />} />
              <Route path='/relationships' element={<RelationshipsPage />} />
              <Route path='/relationships/new' element={<RelationshipWizardPage />} />
              <Route path='/relationships/new/corporate-holdings' element={<WizardCorporateHoldingsPage />} />
              <Route path='/relationships/new/send-invitations' element={<WizardSendInvitationsPage />} />
              <Route path='/resend-invitation' element={<ResendInvitationPage />} />
              <Route path='/relationships/:id' element={<RelationshipDetailPage />} />
              {featureFlags.cashflowWorkspace && (
                <>
                  <Route path='/relationships/:profileId/cashflow' element={<CashflowWorkspacePage />} />
                  <Route path='/relationships/:profileId/cashflow/:scenarioId' element={<CashflowWorkspacePage />} />
                </>
              )}
              <Route path='/relationships/:id/change-of-agency' element={<CoAWizardPage />} />
              <Route path='/relationships/:id/requests/new' element={<NewRequestPage />} />
              <Route path='/relationships/:id/proposals/pension-wizard' element={<PensionProposalWizard />} />
              <Route path='/relationships/:id/proposals/pension-wizard/:proposalId' element={<PensionProposalWizard />} />
              <Route path='/relationships/:profileId/proposals/pension-builder' element={<PensionProposalBuilder />} />
              <Route path='/relationships/:profileId/proposals/pension-builder/:proposalId' element={<PensionProposalBuilder />} />
              <Route path='/relationships/:profileId/proposals/investment-builder' element={<QuoteBackedProposalBuilder proposalType='FUND_INVESTMENT' />} />
              <Route path='/relationships/:profileId/proposals/investment-builder/:proposalId' element={<QuoteBackedProposalBuilder proposalType='FUND_INVESTMENT' />} />
              <Route path='/relationships/:profileId/proposals/protection-builder' element={<QuoteBackedProposalBuilder proposalType='PROTECTION' />} />
              <Route path='/relationships/:profileId/proposals/protection-builder/:proposalId' element={<QuoteBackedProposalBuilder proposalType='PROTECTION' />} />
              <Route path='/relationships/:profileId/proposals/breakthrough-builder/:proposalId' element={<BreakthroughReportBuilder />} />
              <Route path='/relationships/:id/proposals/:proposalId' element={<ProposalDetailPage />} />
              <Route
                path='/relationships/:relationshipId/policies/:policyId/nudge'
                element={<NudgeProviderPage />}
              />
              <Route
                path='/relationships/:relationshipId/coa/:coaId/policies/:policyId/nudge'
                element={<NudgeProviderPage />}
              />
              <Route path='/clients' element={<ClientsListPage />} />
              <Route path='/clients/:clientId' element={<ClientDetailPage />} />
              <Route path='/clients/:clientId/risk' element={<ClientRiskAssessmentPage />} />
              <Route path='/clients/:clientId/sustainability' element={<ClientSustainabilityPage />} />
              <Route path='/clients/:clientId/aml' element={<ClientAmlPage />} />
              <Route path='/clients/:clientId/deep-dive' element={<ClientDeepDivePage />} />
              <Route path='/policies' element={<PoliciesListPage />} />
              <Route path='/policies/:policyId' element={<PolicyDetailPage />} />
              <Route path='/funds' element={<FundsPage />} />
              {/* Static /funds/portfolios* routes outrank the :fundKey param route.
                  The portfolios list lives on the Fund Center page (PortfolioListPanel). */}
              <Route path='/funds/portfolios/new' element={<PortfolioBuilderPage />} />
              <Route path='/funds/portfolios/:id' element={<PortfolioBuilderPage />} />
              <Route path='/funds/:fundKey' element={<FundDetailPage />} />
              <Route path='/providers' element={<ProvidersListPage />} />
              <Route path='/providers/:providerId' element={<ProviderDetailPage />} />
              <Route path='/settings' element={<SettingsPage />} />
              <Route path='/notifications' element={<NotificationsPage />} />
              {/* Support feature routes */}
              <Route path='/support-dashboard' element={<SupportDashboardPage />} />
              <Route path='/support-team' element={<SupportTeamPage />} />
              <Route path='/activity-log' element={<ActivityLogPage />} />
              {/* Admin routes - wrapped in AdminProvider for role checking */}
              <Route path='/admin/dashboard' element={<AdminProvider><AdminDashboardPage /></AdminProvider>} />
              <Route path='/admin/users' element={<AdminProvider><AdvisorManagementPage /></AdminProvider>} />
              <Route path='/admin/advisors' element={<Navigate to='/admin/users' replace />} />
              {/* Redirect old admin URLs to unified pages with view=all */}
              <Route path='/admin/relationships' element={<AdminViewRedirect to="/relationships" />} />
              <Route path='/admin/clients' element={<AdminViewRedirect to="/clients" />} />
              <Route path='/admin/policies' element={<AdminViewRedirect to="/policies" />} />
              <Route path='/admin/loas' element={<AdminProvider><AllLOAsPage /></AdminProvider>} />
              <Route path='/admin/assignments' element={<AdminProvider><AssignmentHistoryPage /></AdminProvider>} />
              <Route path='/admin/tags' element={<AdminProvider><TagsAdminPage /></AdminProvider>} />
              <Route path='*' element={<RoleBasedHomeRedirect />} />
            </Route>
          </Routes>
          <CompanionLauncher />
        </BrowserRouter>
      </BrandingProvider>
    </AnalyticsProvider>
  );
}

export default App;
